import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Payment3dsService } from 'src/app/services/payment-3ds.service';
import Swal from 'sweetalert2';

export interface Payment3dsResult {
  success: boolean;
  clientTracking?: string;
  transactionId?: string;
  authorizationNumber?: string;
  status?: string;
  flowType?: string;
  error?: string;
}

@Component({
  selector: 'app-payment-card-3ds',
  templateUrl: './payment-card-3ds.component.html',
  styleUrls: ['./payment-card-3ds.component.css'],
})
export class PaymentCard3dsComponent implements OnInit, OnDestroy {
  @Input() personCode: string = '';
  @Input() installmentCode: string = '';
  @Input() amountUsd: number = 0;
  @Input() email: string = '';

  @Output() paymentCompleted = new EventEmitter<Payment3dsResult>();
  @Output() paymentCancelled = new EventEmitter<void>();
  @Output() goToPayments = new EventEmitter<void>();

  // Max amount (saved from Input before user edits it)
  maxAmountUsd: number = 0;

  // Steps: 1=widget (tokenizar), 2=CVV+confirmar, 3=procesando, 4=resultado
  step: number = 1;

  // Widget
  widgetLoading: boolean = true;
  widgetError: string = '';
  widgetIframeUrl: SafeResourceUrl | null = null;

  // Token from widget
  accountToken: string = '';
  maskedCard: string = '';
  cardHolderName: string = '';
  cardBrand: string = '';

  // IGTF
  applyIgtf: boolean = false;
  igtfRate: number = 0.03;

  // CVV
  cvv: string = '';

  // Processing
  processing: boolean = false;
  processingMessage: string = 'Procesando pago seguro...';
  manualChecking: boolean = false;

  // Challenge
  challengeUrl: string = '';
  challengeSafeUrl: SafeResourceUrl | null = null;
  showChallenge: boolean = false;
  clientTracking: string = '';
  pollingInterval: any = null;

  // Result
  result: Payment3dsResult | null = null;

  // Message listener reference
  private messageListener: ((event: MessageEvent) => void) | null = null;

  constructor(
    private payment3dsService: Payment3dsService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.maxAmountUsd = this.amountUsd;
    this.loadWidget();
  }

  ngOnDestroy(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
  }

  /**
   * Step 1: Load CROEM widget in an iframe with a bridge page
   */
  async loadWidget() {
    this.widgetLoading = true;
    this.widgetError = '';

    try {
      const config = await this.payment3dsService.getWidgetConfig();
      const widgetData = config?.response || config?.data || config;

      if (!widgetData?.active) {
        this.widgetError = 'Pagos con tarjeta internacional no disponibles en este momento.';
        this.widgetLoading = false;
        return;
      }

      const widgetUrl = widgetData.widgetUrl;
      const apiKey = widgetData.apiKey;
      if (!widgetUrl || !apiKey) {
        this.widgetError = 'Configuracion del widget incompleta.';
        this.widgetLoading = false;
        return;
      }

      // Fetch widget HTML WITH ApiKey parameter (required by CROEM)
      const resp = await fetch(`${widgetUrl}?ApiKey=${apiKey}`);
      if (!resp.ok) {
        throw new Error(`Error cargando widget: ${resp.status}`);
      }
      const widgetHtml = await resp.text();

      // Build bridge: callbacks + style overrides + widget HTML
      const bridgeHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
<style>
  body { margin:0; padding:0; font-family:'Inter',Arial,sans-serif; background:transparent; }
  /* Override CROEM fixed widths */
  .card-ui-component-outer { width:100%!important; max-width:100%!important; box-shadow:none!important; border-radius:0!important; border:none!important; padding:0!important; }
  /* HIDE the blue card preview completely */
  .jp-card-container, .card-wrapper, .jp-card, .jp-card-front, .jp-card-back { display:none!important; height:0!important; overflow:hidden!important; margin:0!important; padding:0!important; }
  .sale { width:100%!important; max-width:100%!important; }
  /* Better inputs */
  .card-ui-component-field {
    height:44px!important; padding:10px 14px!important; font-size:15px!important;
    border:1.5px solid #d1d5db!important; border-radius:8px!important;
    box-shadow:none!important; transition:border-color .2s!important;
  }
  .card-ui-component-field:focus {
    border-color:#3b82f6!important; outline:none!important;
    box-shadow:0 0 0 3px rgba(59,130,246,.15)!important;
  }
  .card-ui-component-field::placeholder { color:#9ca3af!important; }
  /* Row spacing */
  .card-ui-component-row-static { margin-bottom:12px!important; }
  .card-ui-component-input-style { gap:8px!important; }
  /* Buttons */
  .card-ui-component-btn-action {
    height:44px!important; border-radius:8px!important; font-size:15px!important;
    font-weight:600!important; letter-spacing:.3px!important; cursor:pointer!important;
    transition:all .2s!important;
  }
  .card-ui-component-save-btn {
    background:#3b82f6!important; border-color:#3b82f6!important; color:#fff!important;
  }
  .card-ui-component-save-btn:hover { background:#2563eb!important; }
  .card-ui-component-cancel-btn {
    background:#f3f4f6!important; border-color:#d1d5db!important; color:#374151!important;
  }
  .card-ui-component-cancel-btn:hover { background:#e5e7eb!important; }
  .card-ui-component-action-container { gap:12px!important; margin-top:20px!important; }
  /* Alerts */
  .card-ui-component-danger-alert { border-radius:8px!important; font-size:14px!important; }
  .card-ui-component-success-alert { border-radius:8px!important; font-size:14px!important; }
  /* Hide CROEM heading */
  .card-ui-component-heading { display:none!important; }
  /* Validation */
  .card-ui-component-validation-error { border-color:#ef4444!important; }
  /* Labels */
  .card-ui-component-label { font-size:13px!important; font-weight:500!important; color:#374151!important; margin-bottom:4px!important; }
</style>
<script>
function SaveCreditCard_SuccessCallback(r){
  var holderEl=document.getElementById('card-ui-component-txt-creditcard-holder');
  var numEl=document.getElementById('card-ui-component-txt-creditcard-number');
  var holder=holderEl?(holderEl.value||''):'';
  var digits=numEl?String(numEl.value||'').replace(/[^0-9]/g,''):'';
  var last4=digits.slice(-4);
  window.parent.postMessage({type:'CROEM_TOKEN_SUCCESS',payload:r,cardInfo:{holder:holder,last4:last4}},'*');
}
function SaveCreditCard_FailureCallback(e){
  window.parent.postMessage({type:'CROEM_TOKEN_ERROR',payload:e},'*');
}
function SaveCreditCard_CancelCallback(){
  window.parent.postMessage({type:'CROEM_TOKEN_CANCEL'},'*');
}
</script>
</head>
<body>
${widgetHtml}
<script>
document.addEventListener('DOMContentLoaded',function(){
  var h=document.getElementById('card-ui-component-txt-creditcard-holder');
  var n=document.getElementById('card-ui-component-txt-creditcard-number');
  var e=document.getElementById('card-ui-component-ddl-expiry');
  if(h)h.placeholder='Nombre del Titular';
  if(n)n.placeholder='Numero de Tarjeta';
  if(e)e.placeholder='MM/AA';
  var s=document.getElementById('card-ui-component-btn-confirm');
  var c=document.getElementById('card-ui-component-btn-cancel');
  if(s)s.textContent='Guardar Tarjeta';
  if(c)c.textContent='Cancelar';
});
</script>
</body></html>`;

      const blob = new Blob([bridgeHtml], { type: 'text/html' });
      this.widgetIframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));
      this.widgetLoading = false;

      // Listen for messages from the widget iframe
      if (!this.messageListener) {
        this.messageListener = (event: MessageEvent) => {
          if (event.data?.type === 'CROEM_TOKEN_SUCCESS') {
            this.onTokenSuccess(event.data.payload, event.data.cardInfo);
          } else if (event.data?.type === 'CROEM_TOKEN_ERROR') {
            this.onTokenError(event.data.payload);
          } else if (event.data?.type === 'CROEM_TOKEN_CANCEL') {
            this.cancel();
          } else if (event.data?.type === 'CROEM_CHALLENGE_RESULT') {
            this.onChallengeResult(event.data);
          }
        };
        window.addEventListener('message', this.messageListener);
      }

    } catch (error: any) {
      this.widgetError = 'Error cargando el formulario de tarjeta: ' + (error.message || error);
      this.widgetLoading = false;
    }
  }


  /**
   * Widget tokenization success
   */
  onTokenSuccess(response: any, cardInfo?: any) {
    const td = response?.TokenDetails || {};
    this.accountToken = td.AccountToken || response?.Token || response?.token || '';

    // Titular y nº enmascarado: se leen del formulario del widget (cardInfo) —
    // no depende de la estructura de la respuesta CROEM y nunca expone el PAN
    // completo (solo se conservan los últimos 4). Fallback a campos de la respuesta.
    this.cardHolderName = (cardInfo?.holder) || td.CardHolderName || response?.CardHolderName || '';
    if (cardInfo?.last4) {
      this.maskedCard = '**** ' + cardInfo.last4;
      this.cardBrand = '';
    } else {
      this.maskedCard = td.CardNumber || td.MaskedCard || response?.MaskedCard || response?.maskedCard || '';
      const digits = this.maskedCard.replace(/[^0-9]/g, '');
      this.cardBrand = digits.startsWith('4') ? 'visa'
        : (digits.startsWith('5') || digits.startsWith('2')) ? 'mastercard' : '';
    }

    if (!this.accountToken) {
      Swal.fire('Error', 'No se pudo obtener el token de la tarjeta.', 'error');
      return;
    }

    // Move to step 2: CVV
    this.step = 2;
  }

  /**
   * Monto final con o sin IGTF
   */
  get finalAmount(): number {
    const base = this.amountUsd || 0;
    return this.applyIgtf ? Math.round(base * (1 + this.igtfRate) * 100) / 100 : base;
  }

  get igtfAmount(): number {
    return Math.round((this.amountUsd || 0) * this.igtfRate * 100) / 100;
  }

  /**
   * Widget tokenization error
   */
  onTokenError(error: any) {
    const msg = error?.Description || error?.description || JSON.stringify(error);
    Swal.fire('Error de Tarjeta', msg, 'error');
  }

  /**
   * Step 2: Confirm payment with CVV
   */
  async confirmPayment() {
    if (!this.cvv || this.cvv.length < 3) {
      Swal.fire('CVV Requerido', 'Ingrese el codigo de seguridad (3-4 digitos).', 'warning');
      return;
    }

    const payAmount = this.finalAmount;

    const confirm = await Swal.fire({
      title: 'Confirmar Pago',
      html: `
        <p>Monto: <strong>$${payAmount.toFixed(2)} USD</strong></p>
        ${this.applyIgtf ? '<p style="font-size:13px;color:#666;">Incluye IGTF (3%): $' + this.igtfAmount.toFixed(2) + '</p>' : ''}
        <p>Tarjeta: <strong>${this.maskedCard || '****'}</strong></p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Pagar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
    });

    if (!confirm.isConfirmed) return;

    this.step = 3;
    this.processing = true;
    this.processingMessage = 'Autenticando tarjeta...';

    try {
      const idempotencyKey = this.payment3dsService.generateIdempotencyKey();

      const response = await this.payment3dsService.initiatePayment({
        personCode: this.personCode,
        installmentCode: this.installmentCode,
        amountUsd: payAmount,
        accountToken: this.accountToken,
        cvv: this.cvv,
        email: this.email,
        idempotencyKey,
        // Datos ENMASCARADOS de la tarjeta (el PAN nunca sale del widget CROEM),
        // para mostrarlos en el detalle del pago en consola.
        card: {
          maskedPan: this.maskedCard || null,
          holder: this.cardHolderName || null,
          brand: this.cardBrand || null,
        },
      });

      // NOTE: CVV is NOT cleared here — it's needed if challenge flow requires complete-sale.
      // It will be cleared after payment completes or fails.

      const data = response?.response || response?.data || response;

      if (data?.status === 'SALE_COMPLETED') {
        this.onPaymentSuccess(data);
      } else if (data?.status === 'CHALLENGE_PENDING') {
        this.handleChallenge(data);
      } else {
        this.onPaymentError(data?.error || response?.message || 'Error procesando el pago');
      }

    } catch (error: any) {
      // Parse validation errors from kredit_API (express-validator format)
      const body = error?.error;
      let msg = '';
      if (body?.errors && Array.isArray(body.errors)) {
        msg = body.errors.map((e: any) => e.msg).join('. ');
      } else {
        msg = body?.msg || body?.message || error?.message || 'Error de conexion';
      }
      this.onPaymentError(msg);
    }
  }

  /**
   * Handle 3DS Challenge
   */
  handleChallenge(data: any) {
    this.processingMessage = 'Verificacion de seguridad del banco...';
    this.clientTracking = data.clientTracking;
    this.challengeUrl = data.challengeUrl;

    if (this.challengeUrl) {
      this.challengeSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.challengeUrl);
      this.showChallenge = true;
    }

    // Listen for postMessage from challenge iframe
    this.messageListener = (event: MessageEvent) => {
      if (event.data?.type === 'CROEM_CHALLENGE_RESULT') {
        this.onChallengeResult(event.data);
      }
    };
    window.addEventListener('message', this.messageListener);

    // Start polling
    this.startPolling();
  }

  /**
   * Poll transaction status every 5 seconds
   */
  startPolling() {
    this.pollingInterval = setInterval(async () => {
      try {
        const resp = await this.payment3dsService.getStatus(this.clientTracking);
        const data = resp?.response || resp?.data || resp;

        if (data?.status === 'SALE_COMPLETED') {
          this.stopPolling();
          this.onPaymentSuccess(data);
        } else if (data?.status === 'SALE_FAILED' || data?.status === 'EXPIRED') {
          this.stopPolling();
          this.onPaymentError(data?.error || data?.responseDescription || 'Pago rechazado');
        }
      } catch (error) {
        // Ignore polling errors, will retry
      }
    }, 5000);

    // Timeout after 3 minutes
    setTimeout(() => {
      if (this.pollingInterval) {
        this.stopPolling();
        this.processingMessage = 'Su pago esta siendo procesado. Puede verificar el estado despues.';
      }
    }, 180000);
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.showChallenge = false;
  }

  /**
   * Handle challenge result from postMessage
   */
  async onChallengeResult(data: any) {
    this.showChallenge = false;
    this.stopPolling();

    if (data.status === 'success') {
      this.processingMessage = 'Challenge completado. Procesando cobro...';
      // The backend challenge-return endpoint already updated the status.
      // We need to call complete-sale with CVV.
      // But CVV was already cleared — for post-challenge, the backend
      // should have the tdsAuthId. Let's poll for completion.
      this.startPolling();
    } else {
      this.onPaymentError(data.message || 'Verificacion de seguridad fallida');
    }
  }

  /**
   * Manual challenge check — calls complete-sale which internally queries CROEM
   */
  async manualCheckChallenge() {
    if (this.manualChecking) return;
    this.manualChecking = true;
    this.processingMessage = 'Consultando estado del challenge...';

    try {
      const response = await this.payment3dsService.completeSale({
        clientTracking: this.clientTracking,
        cvv: this.cvv,
        personCode: this.personCode,
        installmentCode: this.installmentCode,
        card: {
          maskedPan: this.maskedCard || null,
          holder: this.cardHolderName || null,
          brand: this.cardBrand || null,
        },
      });

      const data = response?.response || response?.data || response;

      if (data?.status === 'SALE_COMPLETED') {
        this.stopPolling();
        this.onPaymentSuccess(data);
      } else if (data?.status === 'SALE_FAILED' || data?.status === 'EXPIRED') {
        this.stopPolling();
        this.onPaymentError(data?.error || data?.responseDescription || 'Pago rechazado por el banco');
      } else {
        // Challenge not yet completed
        this.processingMessage = 'El challenge aun no ha sido completado. Intente de nuevo despues de completar la verificacion del banco.';
      }
    } catch (error: any) {
      const body = error?.error;
      let msg = '';
      if (body?.errors && Array.isArray(body.errors)) {
        msg = body.errors.map((e: any) => e.msg).join('. ');
      } else {
        msg = body?.msg || body?.message || error?.message || 'Error consultando estado';
      }
      this.processingMessage = msg;
    } finally {
      this.manualChecking = false;
    }
  }

  /**
   * Payment success
   */
  onPaymentSuccess(data: any) {
    this.processing = false;
    this.cvv = '';
    this.step = 4;
    this.result = {
      success: true,
      clientTracking: data.clientTracking,
      transactionId: data.transactionId,
      authorizationNumber: data.authorizationNumber,
      status: 'SALE_COMPLETED',
      flowType: data.flowType,
    };
    this.paymentCompleted.emit(this.result);
  }

  /**
   * Payment error
   */
  onPaymentError(msg: string) {
    this.processing = false;
    this.cvv = '';
    this.step = 4;
    this.result = {
      success: false,
      error: msg,
      status: 'SALE_FAILED',
    };
    this.paymentCompleted.emit(this.result);
  }

  /**
   * Cancel and go back
   */
  cancel() {
    this.paymentCancelled.emit();
  }

  /**
   * Reset to step 1
   */
  retry() {
    this.step = 1;
    this.accountToken = '';
    this.maskedCard = '';
    this.cardHolderName = '';
    this.cardBrand = '';
    this.cvv = '';
    this.applyIgtf = false;
    this.result = null;
    this.processing = false;
    this.loadWidget();
  }
}
